/* Вложения карточки: список файлов + загрузка с устройства.
   Файл уходит в /uploads/file, затем привязывается к записи — тем же путём,
   что вложения карточки разработки. Удаление — свайпом строки влево. */

import React, { useRef, useState } from "react";

import { api, avatarUrl, uploadFile } from "../lib/api.js";
import { confirmDialog, haptic } from "../lib/tg.js";
import { Ion } from "./icons.jsx";
import SwipeRow from "./SwipeRow.jsx";
import { Sep } from "./ui.jsx";

/**
 * @param base      корень API-записи: "/deals/42", "/projects/atlant".
 *                  Пусто — записи ещё нет (форма создания): файл всё равно
 *                  загружается на сервер, но привязывать его пока не к чему,
 *                  поэтому загруженное отдаём наружу и держим в форме до сохранения
 * @param items     текущие вложения
 * @param onChanged перезапрос карточки после изменений
 * @param onAdd     без base: загруженный файл { name, kind, url }
 * @param onRemove  без base: убрать файл по его месту в списке
 * @param canEdit   false — только чтение: файлы открываются, но не грузятся и не удаляются
 */
export default function Attachments({ base, items = [], onChanged, onAdd, onRemove, canEdit = true }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setErr(null);
    try {
      for (const file of files) {
        const up = await uploadFile(file);
        if (base) await api.post(`${base}/attachments`, up);
        else onAdd?.(up);
      }
      haptic.success();
      if (base) onChanged?.();
    } catch (e2) {
      haptic.error();
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a, i) => {
    const ok = await confirmDialog(`Удалить «${a.name}»?`);
    if (!ok) return;
    // До сохранения записи файл нигде не привязан — убираем его только из формы
    if (!base) return onRemove?.(i);
    await api.del(`${base}/attachments/${a.id}`);
    haptic.success();
    onChanged?.();
  };

  // В чтении без файлов блоку нечего показывать — пустую карточку не рисуем
  if (!canEdit && !items.length) return null;

  return (
    <div className="px-4 pt-2">
      <div className="card-tg py-1 overflow-hidden">
        {/* У ещё не привязанного файла id нет — ключом идёт его место в списке */}
        {items.map((a, i) => (
          <React.Fragment key={a.id ?? `new-${i}`}>
            {i > 0 ? <Sep /> : null}
            <SwipeRow onDelete={canEdit ? () => remove(a, i) : null} contentClassName="flex items-center gap-3 px-4 py-2.5">
              {a.kind === "image" && a.url ? (
                <img
                  src={avatarUrl(a.url)}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                  style={{ background: "var(--tg-secondary-bg)" }}
                />
              ) : (
                <span
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "var(--tg-secondary-bg)" }}
                >
                  <Ion.documentText size={18} style={{ color: "var(--tg-hint)" }} />
                </span>
              )}
              <a
                data-nodrag
                href={a.url ? avatarUrl(a.url) : undefined}
                target="_blank"
                rel="noreferrer"
                className="text-[14px] truncate flex-1"
                style={{ color: "var(--tg-text)" }}
              >
                {a.name}
              </a>
              <Ion.chevronForward size={15} style={{ color: "var(--tg-hint)" }} className="shrink-0" />
            </SwipeRow>
          </React.Fragment>
        ))}

        {items.length && canEdit ? <Sep /> : null}
        {canEdit ? (
        <button
          className="w-full flex items-center gap-3 px-4 py-2.5 active:opacity-60 disabled:opacity-40"
          onClick={() => input.current?.click()}
          disabled={busy}
        >
          <span
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)" }}
          >
            <Ion.attach size={18} style={{ color: "var(--tg-button)" }} />
          </span>
          <span className="text-[14px] font-medium" style={{ color: "var(--tg-button)" }}>
            {busy ? "Загружаем…" : "Прикрепить файл"}
          </span>
        </button>
        ) : null}
        {err ? (
          <div className="px-4 pb-2 text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
        ) : null}
      </div>

      <input ref={input} type="file" multiple hidden onChange={pick} />
    </div>
  );
}
